import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { TESTS, HOLTER, HOLTER_PRICES } from "../../data/fees";
import { SlotPicker, fmtSlot } from "../booking/SlotPicker";
import { HolterDays } from "../booking/HolterDays";

const input = "w-full rounded-sm border border-stone-300 bg-white px-3 py-2 text-sm";

const Result = ({ r }) => (
  <div data-testid="staff-test-result" className="p-4 bg-cream-100 border border-bronze-light/50 rounded-md text-sm space-y-2">
    <p><b>Reserved for 24 hours:</b> {r.when} · £{(r.total / 100).toFixed(2)} · ref {r.booking_ref}</p>
    <p>{r.emailed ? "Payment link emailed to the patient." : "Not emailed - copy the link below or open it on the clinic computer."}</p>
    <div className="flex gap-3 flex-wrap">
      <button type="button" className="btn-secondary" data-testid="staff-test-copy" onClick={() => navigator.clipboard.writeText(r.url).then(() => toast.success("Link copied."))}>Copy link</button>
      <a href={r.url} target="_blank" rel="noopener noreferrer" className="btn-secondary" data-testid="staff-test-open">Pay now on this computer</a>
    </div>
  </div>
);

export const StaffTestBooking = ({ onCreated }) => {
  const [refresh, setRefresh] = useState(0);
  const [f, setF] = useState({ name: "", email: "", phone: "", send_email: true });
  const [tests, setTests] = useState([]);
  const [days, setDays] = useState(1);
  const [slot, setSlot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const ordered = Object.keys(TESTS).filter((t) => tests.includes(t));
  const lines = ordered.map((t) => (t === HOLTER ? { label: `Holter (${days} day${days > 1 ? "s" : ""})`, price: HOLTER_PRICES[days] } : TESTS[t]));
  const total = lines.reduce((a, l) => a + l.price, 0);
  const toggle = (t) => { setSlot(null); setTests(tests.includes(t) ? tests.filter((x) => x !== t) : [...tests, t]); };
  const submit = (e) => {
    e.preventDefault();
    if (!slot) return toast.error("Please choose a time.");
    setBusy(true);
    api.post("/admin/test-bookings", { ...f, tests: ordered, holter_days: days, start_time: slot })
      .then(({ data }) => { setResult(data); setSlot(null); setRefresh((n) => n + 1); onCreated?.(); toast.success("Appointment reserved."); })
      .catch((err) => toast.error(formatApiErrorDetail(err.response?.data?.detail))).finally(() => setBusy(false));
  };
  return (
    <form onSubmit={submit} data-testid="staff-test-booking" className="bg-white border border-stone-200 rounded-md p-6 space-y-5">
      <div>
        <h2 className="font-serif text-2xl">Book tests for a patient</h2>
        <p className="text-sm text-ink-muted">For tests ordered after a consultation. Echo times come from Navazh&apos;s diary and ECG, ABPM, Holter and bioimpedance from the nurse&apos;s. The time is held for 24 hours while the patient pays by link.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {[["name", "Patient name", "text"], ["email", "Patient email", "email"], ["phone", "Patient phone", "tel"]].map(([k, l, t]) => (
          <label key={k} className="text-sm">{l}<input required type={t} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={input} data-testid={`staff-test-${k}`} /></label>
        ))}
      </div>
      <fieldset className="flex flex-wrap gap-x-6 gap-y-2 text-sm"><legend className="text-sm font-medium mb-2">Tests</legend>
        {Object.entries(TESTS).map(([k, t]) => (
          <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={tests.includes(k)} onChange={() => toggle(k)} data-testid={`staff-test-check-${k}`} />{t.label}</label>
        ))}
      </fieldset>
      <HolterDays reasons={tests} value={days} onChange={setDays} />
      {tests.length > 0 && <>
        <SlotPicker value={slot} onChange={setSlot} holterDays={days} consultant="any" tests={ordered} visit="tests_only" step={2} anyTests refreshKey={refresh} />
        <p className="text-sm" data-testid="staff-test-summary">{lines.map((x) => `${x.label} £${x.price}`).join(" + ")} = <b>£{total}</b>{slot && ` · ${fmtSlot(slot)}`}</p>
      </>}
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.send_email} onChange={(e) => setF({ ...f, send_email: e.target.checked })} data-testid="staff-test-send-email" />Email the payment link to the patient</label>
      <button type="submit" disabled={busy || !tests.length} className="btn-primary" data-testid="staff-test-submit">{busy ? "Reserving…" : "Reserve and create payment link"}</button>
      {result && <Result r={result} />}
    </form>
  );
};
